<template>
  <v-menu offset-y>
    <template v-slot:activator="{ props }">
      <v-btn
        v-bind="props"
        variant="text"
        size="small"
        class="text-none"
        prepend-icon="mdi-translate"
        :title="`${t('common.currentLanguage')}: ${currentLanguageName}`"
      >
        {{ currentLanguageName }}
        <v-icon end class="ml-1">mdi-chevron-down</v-icon>
      </v-btn>
    </template>
    
    <v-list>
      <v-list-item
        v-for="lang in availableLanguages"
        :key="lang.code"
        @click="changeLanguage(lang.code)"
        :active="currentLocale === lang.code"
        :class="{ 'v-list-item--active': currentLocale === lang.code }"
      >
        <template #prepend>
          <v-icon 
            :color="currentLocale === lang.code ? 'primary' : 'grey'"
            size="small"
          >
            {{ currentLocale === lang.code ? 'mdi-check' : 'mdi-translate' }}
          </v-icon>
        </template>
        <v-list-item-title>{{ lang.name }}</v-list-item-title>
        <v-list-item-subtitle v-if="lang.code === 'en'">
          {{ t('common.default') }}
        </v-list-item-subtitle>
        <v-list-item-subtitle v-else-if="lang.code === 'es'">
          {{ t('common.native') }}
        </v-list-item-subtitle>
        <v-list-item-subtitle v-else-if="lang.code === 'pt'">
          {{ t('common.native') }}
        </v-list-item-subtitle>
        <v-list-item-subtitle v-if="lang.code === browserLanguage && lang.code !== currentLocale">
          {{ t('common.browserDetected') }}
        </v-list-item-subtitle>
      </v-list-item>
      
      <v-divider class="my-2"></v-divider>
      
      <v-list-item>
        <v-list-item-title class="text-caption text-medium-emphasis">
          {{ t('common.browserLanguage') }}: {{ browserLanguageName }}
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

const { locale, t } = useI18n()

const currentLocale = computed(() => locale.value)
const browserLanguage = ref('en')

// Detectar idioma del navegador al montar el componente
onMounted(() => {
  const browserLang = navigator.language || navigator.languages?.[0] || 'en'
  const primaryLang = browserLang.split('-')[0]
  
  // Verificar si el idioma del navegador está soportado
  if (['en', 'es', 'pt'].includes(primaryLang)) {
    browserLanguage.value = primaryLang
  }
})

const availableLanguages = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'pt', name: 'Português' }
]

const currentLanguageName = computed(() => {
  const lang = availableLanguages.find(l => l.code === currentLocale.value)
  return lang ? lang.name : 'English'
})

const browserLanguageName = computed(() => {
  const lang = availableLanguages.find(l => l.code === browserLanguage.value)
  return lang ? lang.name : 'English'
})

const changeLanguage = (langCode: string) => {
  locale.value = langCode
  
  // Guardar preferencia en localStorage
  localStorage.setItem('preferred-language', langCode)
}

// Función para obtener el idioma preferido del usuario
const getPreferredLanguage = () => {
  // Primero intentar obtener del localStorage
  const saved = localStorage.getItem('preferred-language')
  if (saved && ['en', 'es', 'pt'].includes(saved)) {
    return saved
  }
  
  // Si no hay preferencia guardada, usar el idioma del navegador
  return browserLanguage.value
}

// Función para inicializar el idioma
const initializeLanguage = () => {
  const preferred = getPreferredLanguage()
  if (preferred !== currentLocale.value) {
    locale.value = preferred
  }
}

// Inicializar idioma al montar
onMounted(() => {
  initializeLanguage()
})
</script>

<style scoped>
.v-list-item--active {
  background-color: rgba(var(--v-theme-primary), 0.1);
}

.v-list-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.05);
}

.v-btn {
  min-width: 120px;
}

.v-list-item {
  cursor: pointer;
}

.v-list-item-subtitle {
  font-size: 0.75rem;
  opacity: 0.7;
}
</style>
